<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationApi from '../../../api/inbox/conversation';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
      localProperties: {},
      dropdownOpen: null,
      dropdownSearch: '',
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    enabledConversationFields() {
      return this.$store.getters['conversationFieldDefinitions/getEnabledConversationFields'];
    },
    conversationProperties() {
      return this.currentChat.conversation_properties || {};
    },
    hasChanges() {
      const orig = this.conversationProperties;
      const local = this.localProperties;
      const keys = new Set([...Object.keys(orig), ...Object.keys(local)]);
      for (const key of keys) {
        // eslint-disable-next-line eqeqeq
        if (orig[key] != local[key]) return true;
      }
      return false;
    },
  },
  watch: {
    conversationProperties: {
      immediate: true,
      handler(val) {
        this.localProperties = { ...val };
      },
    },
    'currentChat.id'() {
      this.dropdownOpen = null;
      this.dropdownSearch = '';
    },
  },
  created() {
    this.$store.dispatch('conversationFieldDefinitions/fetchAll');
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },

    setLocalProperty(fieldKey, value) {
      this.localProperties = { ...this.localProperties, [fieldKey]: value };
    },

    getSelectedLabel(field) {
      const val = this.localProperties[field.field_key];
      if (!val) return '';
      const opt = (field.options || []).find(o => o.value === val);
      return opt ? opt.label : val;
    },

    filteredOptions(field) {
      const opts = field.options || [];
      if (!this.dropdownSearch) return opts;
      const q = this.dropdownSearch.toLowerCase();
      return opts.filter(o => o.label.toLowerCase().includes(q));
    },

    toggleDropdown(fieldKey) {
      if (this.dropdownOpen === fieldKey) {
        this.dropdownOpen = null;
        this.dropdownSearch = '';
      } else {
        this.dropdownOpen = fieldKey;
        this.dropdownSearch = '';
      }
    },

    selectOption(fieldKey, value) {
      this.localProperties = { ...this.localProperties, [fieldKey]: value };
      this.dropdownOpen = null;
      this.dropdownSearch = '';
    },

    handleClickOutside(e) {
      if (this.dropdownOpen && this.$el && !this.$el.contains(e.target)) {
        this.dropdownOpen = null;
        this.dropdownSearch = '';
      }
    },

    async saveAllProperties() {
      this.isSaving = true;
      try {
        await ConversationApi.updateConversationProperties({
          conversationId: this.currentChat.id,
          conversationProperties: this.localProperties,
        });
        this.$store.commit('UPDATE_CONVERSATION', {
          ...this.currentChat,
          conversation_properties: this.localProperties,
          updated_at: new Date().toISOString(),
        });
        useAlert('Properties updated successfully');
      } catch {
        useAlert('Failed to update properties');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />

    <!-- Custom conversation properties -->
    <template v-if="enabledConversationFields.length">
      <div
        v-for="field in enabledConversationFields"
        :key="field.field_key"
        class="mt-3"
      >
        <ContactDetailsItem compact :title="field.label" />

        <!-- text -->
        <input
          v-if="field.field_type === 'text'"
          :value="localProperties[field.field_key] || ''"
          type="text"
          class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          @input="setLocalProperty(field.field_key, $event.target.value)"
        />

        <!-- textarea -->
        <textarea
          v-else-if="field.field_type === 'textarea'"
          :value="localProperties[field.field_key] || ''"
          rows="3"
          class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand resize-none"
          @input="setLocalProperty(field.field_key, $event.target.value)"
        />

        <!-- number / decimal -->
        <input
          v-else-if="['number', 'decimal'].includes(field.field_type)"
          :value="localProperties[field.field_key] || ''"
          :step="field.field_type === 'decimal' ? 'any' : '1'"
          type="number"
          class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          @input="setLocalProperty(field.field_key, $event.target.value)"
        />

        <!-- date -->
        <input
          v-else-if="field.field_type === 'date'"
          :value="localProperties[field.field_key] || ''"
          type="date"
          class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          @change="setLocalProperty(field.field_key, $event.target.value)"
        />

        <!-- list (searchable custom dropdown) -->
        <div
          v-else-if="field.field_type === 'list'"
          class="relative"
        >
          <button
            type="button"
            class="w-full flex items-center justify-between px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            :class="{ 'ring-1 ring-n-brand border-n-brand': dropdownOpen === field.field_key }"
            @click.stop="toggleDropdown(field.field_key)"
          >
            <span :class="getSelectedLabel(field) ? 'text-n-slate-12' : 'text-n-slate-9'">
              {{ getSelectedLabel(field) || 'Select...' }}
            </span>
            <span
              class="size-4 shrink-0 text-n-slate-9 transition-transform"
              :class="dropdownOpen === field.field_key ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            />
          </button>

          <div
            v-if="dropdownOpen === field.field_key"
            class="absolute left-0 right-0 top-full mt-1 z-50 rounded border border-n-weak bg-n-solid-1 shadow-md"
          >
            <!-- search -->
            <div class="p-2 border-b border-n-weak">
              <input
                v-model="dropdownSearch"
                type="text"
                placeholder="Search..."
                class="w-full px-2 py-1 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                @click.stop
              />
            </div>
            <!-- options -->
            <div class="max-h-48 overflow-y-auto">
              <button
                v-if="!dropdownSearch"
                type="button"
                class="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-n-alpha-2 text-n-slate-9 italic"
                @click.stop="selectOption(field.field_key, '')"
              >
                — None —
              </button>
              <button
                v-for="opt in filteredOptions(field)"
                :key="opt.value"
                type="button"
                class="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-n-alpha-2"
                :class="localProperties[field.field_key] === opt.value
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : 'text-n-slate-12'"
                @click.stop="selectOption(field.field_key, opt.value)"
              >
                {{ opt.label }}
              </button>
              <p
                v-if="filteredOptions(field).length === 0"
                class="px-3 py-2 text-sm text-n-slate-9"
              >
                No results
              </p>
            </div>
          </div>
        </div>

        <!-- multiselect -->
        <select
          v-else-if="field.field_type === 'multiselect'"
          multiple
          :value="localProperties[field.field_key] || []"
          class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          @change="setLocalProperty(field.field_key, Array.from($event.target.selectedOptions).map(o => o.value))"
        >
          <option
            v-for="opt in field.options"
            :key="opt.value"
            :value="opt.value"
          >{{ opt.label }}</option>
        </select>

        <!-- checkbox -->
        <label
          v-else-if="field.field_type === 'checkbox'"
          class="flex items-center gap-2 cursor-pointer mt-1"
        >
          <input
            type="checkbox"
            :checked="!!localProperties[field.field_key]"
            class="rounded border-n-weak"
            @change="setLocalProperty(field.field_key, $event.target.checked)"
          />
          <span class="text-sm text-n-slate-11">{{ field.label }}</span>
        </label>
      </div>

      <!-- Update button -->
      <button
        type="button"
        class="mt-4 w-full py-1.5 px-3 text-sm font-medium rounded transition-colors"
        :class="hasChanges && !isSaving
          ? 'bg-n-brand text-white hover:bg-n-brand/90 cursor-pointer'
          : 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'"
        :disabled="!hasChanges || isSaving"
        @click="saveAllProperties"
      >
        {{ isSaving ? 'Saving...' : 'Update' }}
      </button>
    </template>
  </div>
</template>
